import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CSSProperties } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { Timestamp } from 'firebase/firestore';
import {
  AppOfferBanner,
  AppOfferBannerContentAlign,
  AppOfferBannerCtaButtonRadius,
  AppOfferBannerCtaButtonVariant,
  AppOfferBannerCtaStyle,
  AppOfferBannerCtaTarget,
  AppOfferBannerInput,
  type BannerAnalyticsCounts,
  bannerActionsClusterClass,
  bannerContentClusterClass,
  bannerMessageTextClass,
  bannerOuterLayoutClass,
  clampDismissHideDays,
  createAppOfferBanner,
  DEFAULT_DISMISS_HIDE_DAYS,
  deleteAppOfferBanner,
  getBannerEventCounts,
  getCtaButtonPresentation,
  listAppOfferBannersAdmin,
  normalizeContentAlign,
  normalizeCtaButtonRadius,
  normalizeCtaButtonVariant,
  normalizeShowBadge,
  normalizeShowCta,
  safeBannerBackgroundImageUrl,
  updateAppOfferBanner,
} from '../../services/appOfferBannerService';
import { uploadFileToS3 } from '../../services/s3Upload';

const PROMO_TOKEN = '{{PROMO}}';

/** ~48–80px tall bar + cover crop — wide, shallow art works best */
const BANNER_BG_SIZE_HINT =
  'Recommended: about 1920×400 px to 2400×480 px (wide strip, roughly 5:1 width to height). The bar is short and the image uses cover, so keep important content near the vertical center.';

const BANNER_BG_MAX_BYTES = 5 * 1024 * 1024;

function isAllowedBannerBackgroundUpload(file: File): boolean {
  const t = file.type.toLowerCase();
  if (t === 'image/svg+xml') return false;
  if (t.startsWith('image/')) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ['jpg', 'jpeg', 'png', 'webp', 'gif', 'avif', 'heic', 'heif'].includes(ext);
}

/** Banner strip fill: image cover when URL set, otherwise CSS background. */
function offerBannerFillStyle(backgroundImageUrl: string, backgroundCss: string): CSSProperties {
  const bgImg = safeBannerBackgroundImageUrl(backgroundImageUrl.trim());
  if (bgImg) {
    return {
      backgroundColor: '#0f2744',
      backgroundImage: `url(${JSON.stringify(bgImg)})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      backgroundRepeat: 'no-repeat',
    };
  }
  return { background: backgroundCss.trim() || '#0f2744' };
}

const defaultForm = () => ({
  isActive: false,
  priority: 10,
  startsAtInput: '',
  endsAtInput: '',
  pathRulesText: '',
  badgeText: 'LAUNCH OFFER',
  badgeBgColor: '#22c55e',
  badgeTextColor: '#ffffff',
  messageTextColor: '#ffffff',
  ctaTextColor: '#ffffff',
  ctaButtonBorderColor: '#ffffff',
  backgroundCss: 'linear-gradient(90deg, #0f2744 0%, #1e3a5f 100%)',
  backgroundImageUrl: '',
  message: '85% off your first paid month — use code {{PROMO}} at checkout.',
  promoCode: 'FIRST85',
  ctaLabel: 'View plans',
  ctaUrl: '/account/billing',
  ctaTarget: 'url' as AppOfferBannerCtaTarget,
  ctaStyle: 'button' as AppOfferBannerCtaStyle,
  contentAlign: 'left' as AppOfferBannerContentAlign,
  showBadge: true,
  showCta: true,
  ctaButtonVariant: 'outline' as AppOfferBannerCtaButtonVariant,
  ctaButtonRadius: 'md' as AppOfferBannerCtaButtonRadius,
  ctaButtonBgColor: 'rgba(255,255,255,0.22)',
  dismissible: true,
  dismissHideDays: DEFAULT_DISMISS_HIDE_DAYS,
});

type FormState = ReturnType<typeof defaultForm>;

function tsToDatetimeLocal(t: Timestamp | null | undefined): string {
  const d = timestampLikeToDate(t);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function datetimeLocalToTs(s: string): Timestamp | null {
  if (!s.trim()) return null;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return Timestamp.fromDate(d);
}

/** Accepts client `Timestamp`, serialized `{ seconds }`, and `Date` (e.g. from Firestore or JSON). */
function timestampLikeToDate(t: unknown): Date | null {
  if (t == null) return null;
  if (t instanceof Date && !Number.isNaN(t.getTime())) return t;
  if (typeof t === 'object' && t !== null && typeof (t as { toDate?: () => Date }).toDate === 'function') {
    try {
      const d = (t as { toDate: () => Date }).toDate();
      if (d instanceof Date && !Number.isNaN(d.getTime())) return d;
    } catch {
      /* fall through */
    }
  }
  if (typeof t === 'object' && t !== null) {
    const o = t as { seconds?: unknown; _seconds?: unknown };
    const sec =
      typeof o.seconds === 'number' ? o.seconds : typeof o._seconds === 'number' ? o._seconds : null;
    if (sec != null) {
      const d = new Date(sec * 1000);
      if (!Number.isNaN(d.getTime())) return d;
    }
  }
  return null;
}

function formatFirestoreTimestamp(t: unknown): string {
  const d = timestampLikeToDate(t);
  if (!d) return '—';
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function parsePathRules(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

function validateCtaUrl(url: string): string | null {
  const u = url.trim();
  if (!u) return null;
  if (u.startsWith('/')) return null;
  if (/^https:\/\//i.test(u)) return null;
  if (/^http:\/\//i.test(u)) return null;
  return 'CTA URL must start with / or https://';
}

function formatFirestoreSaveError(err: unknown): string {
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code?: string }).code);
    if (code === 'permission-denied') {
      return 'Could not save (permission denied). If you were invited as admin, sign out and sign in once, then try again.';
    }
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return 'Something went wrong while saving.';
}

/** `input[type=color]` requires #rrggbb */
function toColorInputValue(hex: string): string {
  const t = hex.trim();
  if (/^#[0-9A-Fa-f]{6}$/i.test(t)) return t;
  if (/^#[0-9A-Fa-f]{3}$/i.test(t)) {
    const r = t[1],
      g = t[2],
      b = t[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return '#000000';
}

function PromoChipPreview({ code, textColor }: { code: string; textColor: string }) {
  return (
    <span
      className="inline-flex items-center mx-0.5 px-1.5 py-0.5 rounded bg-black/30 font-semibold text-xs border border-white/10"
      style={{ color: textColor }}
    >
      {code}
    </span>
  );
}

function previewMessage(message: string, promoCode: string, messageTextColor: string): React.ReactNode {
  const code = promoCode.trim();
  if (code && message.includes(PROMO_TOKEN)) {
    const parts = message.split(PROMO_TOKEN);
    return (
      <>
        {parts.map((part, i) => (
          <React.Fragment key={i}>
            {part}
            {i < parts.length - 1 ? <PromoChipPreview code={code} textColor={messageTextColor} /> : null}
          </React.Fragment>
        ))}
      </>
    );
  }
  if (code) {
    return (
      <>
        {message}
        {message.trim().length > 0 ? ' ' : null}
        <PromoChipPreview code={code} textColor={messageTextColor} />
      </>
    );
  }
  return message;
}

function SectionTitle({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
      <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-200/80 text-slate-600 dark:bg-slate-700/80 dark:text-slate-300">
        {icon}
      </span>
      {children}
    </div>
  );
}

const defaultBannerAnalytics: BannerAnalyticsCounts = {
  impressions: 0,
  dismissClicks: 0,
  ctaClicks: 0,
  ctaClicksLink: 0,
  ctaClicksButton: 0,
  ctaClicksUpgradeModal: 0,
  ctaClicksLegacy: 0,
};

function BannerReportModal({
  banner: b,
  counts,
  analyticsRefreshing,
  analyticsError,
  onClose,
  isDark,
}: {
  banner: AppOfferBanner;
  counts: BannerAnalyticsCounts | undefined;
  analyticsRefreshing: boolean;
  analyticsError: string | null;
  onClose: () => void;
  isDark: boolean;
}) {
  const rc = counts ?? defaultBannerAnalytics;
  const pathRules = Array.isArray(b.pathRules) ? b.pathRules : [];
  const pathLine = pathRules.length ? pathRules.join(' · ') : 'All routes';
  const ctaTargetNorm = b.ctaTarget === 'upgrade_modal' ? 'upgrade_modal' : 'url';
  const ctaTargetLabel = ctaTargetNorm === 'upgrade_modal' ? 'Upgrade modal' : 'URL';
  const ctaStyleLabel = b.ctaStyle === 'link' ? 'Link' : 'Button';
  const showCta = b.showCta !== false;
  const showBadge = b.showBadge !== false;
  const bgImg = safeBannerBackgroundImageUrl(b.backgroundImageUrl || '');
  const cssBg = (b.backgroundCss || '').trim();
  const bgSummary = bgImg ? 'Image (cover)' : cssBg ? (cssBg.length > 100 ? `${cssBg.slice(0, 100)}…` : cssBg) : '—';
  const panel = isDark ? 'border-slate-600 bg-slate-800/50' : 'border-slate-100 bg-slate-50/90';
  const rowDt = 'text-slate-500 dark:text-slate-400';
  const rowDd = 'text-right font-medium text-slate-800 dark:text-slate-100';

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" aria-label="Close report" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="banner-report-title"
        className={`relative max-h-[min(85vh,36rem)] w-full max-w-md overflow-y-auto rounded-2xl border p-5 shadow-2xl sm:p-6 ${
          isDark ? 'border-slate-600 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="banner-report-title" className="text-lg font-bold tracking-tight">
              Banner report
            </h2>
            <p className="mt-1 break-all font-mono text-[11px] text-slate-500 dark:text-slate-400">{b.id}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-4 text-sm">
          <div className={`rounded-xl border p-3 ${panel}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Status</p>
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Active</dt>
                <dd className={rowDd}>{b.isActive ? 'Yes' : 'No'}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Priority</dt>
                <dd className={rowDd}>{b.priority ?? 0}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Paths</dt>
                <dd className={`max-w-[65%] break-words ${rowDd}`}>{pathLine}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Starts</dt>
                <dd className={rowDd}>{formatFirestoreTimestamp(b.startsAt)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Ends</dt>
                <dd className={rowDd}>{formatFirestoreTimestamp(b.endsAt)}</dd>
              </div>
            </dl>
          </div>

          <div className={`rounded-xl border p-3 ${panel}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Analytics</p>
            <p className="mb-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">Totals from logged events (not unique users).</p>
            {analyticsRefreshing ? (
              <p className="mb-2 text-xs font-medium text-cyan-700 dark:text-cyan-300">Refreshing counts from the server…</p>
            ) : null}
            {analyticsError ? (
              <p className="mb-2 text-xs text-amber-800 dark:text-amber-200" role="status">
                {analyticsError} Showing the last loaded totals below.
              </p>
            ) : null}
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Impressions</dt>
                <dd className={`tabular-nums ${rowDd}`}>{rc.impressions}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Close / dismiss</dt>
                <dd className={`tabular-nums ${rowDd}`}>{rc.dismissClicks}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>CTA clicks (total)</dt>
                <dd className={`tabular-nums ${rowDd}`}>{rc.ctaClicks}</dd>
              </div>
              {rc.ctaClicks > 0 ? (
                <div className="border-t border-slate-200/80 pt-2 text-xs dark:border-slate-600">
                  <p className="text-slate-500 dark:text-slate-400">Breakdown</p>
                  <ul className="mt-1 space-y-0.5 tabular-nums text-slate-700 dark:text-slate-300">
                    <li>Link: {rc.ctaClicksLink}</li>
                    <li>Button: {rc.ctaClicksButton}</li>
                    <li>Upgrade modal: {rc.ctaClicksUpgradeModal}</li>
                    {rc.ctaClicksLegacy > 0 ? <li>Unlabeled (legacy): {rc.ctaClicksLegacy}</li> : null}
                  </ul>
                </div>
              ) : null}
            </dl>
          </div>

          <div className={`rounded-xl border p-3 ${panel}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Message</p>
            <p className="whitespace-pre-wrap break-words text-slate-800 dark:text-slate-100">{b.message}</p>
            {b.promoCode?.trim() ? (
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Promo code: <span className="font-mono font-medium text-slate-700 dark:text-slate-300">{b.promoCode.trim()}</span>
              </p>
            ) : null}
          </div>

          <div className={`rounded-xl border p-3 ${panel}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Call to action</p>
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Shown</dt>
                <dd className={rowDd}>{showCta ? 'Yes' : 'No'}</dd>
              </div>
              {showCta ? (
                <>
                  <div className="flex justify-between gap-4">
                    <dt className={rowDt}>Target</dt>
                    <dd className={rowDd}>{ctaTargetLabel}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className={rowDt}>Style</dt>
                    <dd className={rowDd}>{ctaStyleLabel}</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className={rowDt}>Label</dt>
                    <dd className={`max-w-[65%] break-words ${rowDd}`}>{b.ctaLabel?.trim() || '—'}</dd>
                  </div>
                  {ctaTargetNorm !== 'upgrade_modal' ? (
                    <div className="flex justify-between gap-4">
                      <dt className={rowDt}>URL</dt>
                      <dd className={`max-w-[65%] break-all font-mono text-xs ${rowDd}`}>{b.ctaUrl?.trim() || '—'}</dd>
                    </div>
                  ) : null}
                  {b.ctaStyle === 'button' ? (
                    <div className="flex justify-between gap-4">
                      <dt className={rowDt}>Button</dt>
                      <dd className={`text-xs ${rowDd}`}>
                        {normalizeCtaButtonVariant(b.ctaButtonVariant)} · {normalizeCtaButtonRadius(b.ctaButtonRadius)}
                      </dd>
                    </div>
                  ) : null}
                </>
              ) : null}
            </dl>
          </div>

          <div className={`rounded-xl border p-3 ${panel}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Layout & behavior</p>
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Content align</dt>
                <dd className={rowDd}>{normalizeContentAlign(b.contentAlign)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Badge</dt>
                <dd className={rowDd}>{showBadge ? (b.badgeText?.trim() || '(empty)') : 'Hidden'}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Dismissible</dt>
                <dd className={rowDd}>
                  {b.dismissible ? `Yes (${clampDismissHideDays(b.dismissHideDays)} days)` : 'No'}
                </dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className={rowDt}>Background</dt>
                <dd className="break-words font-mono text-xs text-slate-700 dark:text-slate-300">{bgSummary}</dd>
              </div>
            </dl>
          </div>

          <div className={`rounded-xl border p-3 ${panel}`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Record</p>
            <dl className="space-y-1.5">
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Created</dt>
                <dd className={`text-xs ${rowDd}`}>{formatFirestoreTimestamp(b.createdAt)}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className={rowDt}>Updated</dt>
                <dd className={`text-xs ${rowDd}`}>{formatFirestoreTimestamp(b.updatedAt)}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange,
  inputClass,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  inputClass: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">{label}</label>
      <div className="flex gap-2 items-stretch">
        <input
          type="color"
          aria-label={`${label} picker`}
          value={toColorInputValue(value)}
          onChange={(e) => onChange(e.target.value)}
          className="h-11 w-14 shrink-0 cursor-pointer rounded-xl border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-600 dark:bg-slate-800"
        />
        <input type="text" className={`${inputClass} font-mono text-sm`} value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    </div>
  );
}

function BannerLivePreview({
  form,
  isDark,
}: {
  form: FormState;
  isDark: boolean;
}) {
  const align = normalizeContentAlign(form.contentAlign);
  const hasCta =
    form.showCta &&
    Boolean(form.ctaLabel.trim() && (form.ctaTarget === 'upgrade_modal' || form.ctaUrl.trim()));
  const hasBadge = form.showBadge && Boolean(form.badgeText.trim());
  const isLink = form.ctaStyle === 'link';

  const messageTextColor = (form.messageTextColor || '').trim() || '#ffffff';
  const ctaTextColor = (form.ctaTextColor || '').trim() || '#ffffff';
  const ctaButtonBorderColor = (form.ctaButtonBorderColor || '').trim() || 'rgba(255,255,255,0.9)';
  const ctaButtonBgColor = (form.ctaButtonBgColor || '').trim();

  const ctaPres = !isLink
    ? getCtaButtonPresentation(
        normalizeCtaButtonVariant(form.ctaButtonVariant),
        normalizeCtaButtonRadius(form.ctaButtonRadius),
        { ctaTextColor, ctaButtonBorderColor, ctaButtonBgColor }
      )
    : null;

  const ctaLinkClass =
    'inline-flex items-center gap-1 text-sm font-medium underline underline-offset-4 shadow-sm transition';
  const ctaLinkStyle = { color: ctaTextColor, textDecorationColor: ctaTextColor } as CSSProperties;

  const ctaCls = isLink ? ctaLinkClass : (ctaPres?.className ?? '');
  const ctaSt = (isLink ? ctaLinkStyle : (ctaPres?.style ?? {})) as CSSProperties;

  return (
    <div
      className={`overflow-hidden rounded-xl border shadow-md ${
        isDark ? 'border-slate-600 bg-slate-950' : 'border-slate-300 bg-white'
      }`}
    >
      <div
        className={`flex items-center justify-between border-b px-4 py-2 sm:px-5 ${
          isDark ? 'border-slate-700 bg-slate-900/90' : 'border-slate-200 bg-slate-50'
        }`}
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">Banner preview</span>
        <span className="hidden text-[10px] text-slate-400 sm:inline">As shown below the app header</span>
      </div>
      <div className="relative overflow-hidden">
        <div
          className={`relative z-10 px-4 py-3 sm:px-6 sm:py-3.5 ${bannerOuterLayoutClass(align)}`}
          style={offerBannerFillStyle(form.backgroundImageUrl, form.backgroundCss)}
        >
          <div className={bannerContentClusterClass(align)}>
            {hasBadge ? (
              <span
                className="inline-flex shrink-0 items-center self-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide sm:text-xs"
                style={{
                  backgroundColor: form.badgeBgColor || '#22c55e',
                  color: form.badgeTextColor || '#ffffff',
                }}
              >
                {form.badgeText.trim()}
              </span>
            ) : null}
            <p
              className={`min-w-0 flex-1 text-xs leading-snug sm:text-sm sm:leading-normal ${bannerMessageTextClass(align)}`}
              style={{ color: messageTextColor }}
            >
              {previewMessage(form.message || '', form.promoCode || '', messageTextColor)}
            </p>
          </div>
          <div className={bannerActionsClusterClass(align)}>
            {form.dismissible ? (
              <span className="rounded-md p-1.5" style={{ color: messageTextColor, opacity: 0.55 }} title="Close (shown to users)">
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </span>
            ) : null}
            {hasCta ? (
              <span className={ctaCls} style={ctaSt}>
                {form.ctaLabel.trim()}
                {form.ctaTarget === 'upgrade_modal' ? null : <span aria-hidden>→</span>}
              </span>
            ) : form.showCta ? (
              <span className="text-[10px] sm:text-xs" style={{ color: messageTextColor, opacity: 0.45 }}>
                No CTA (add label{form.ctaTarget === 'url' ? ' and URL' : ''})
              </span>
            ) : (
              <span className="text-[10px] sm:text-xs" style={{ color: messageTextColor, opacity: 0.45 }}>
                Announcement only
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const AdminAppOfferBannerManager: React.FC = () => {
  const { showError, showSuccess } = useNotification();
  const { isDark } = useAdminTheme();
  const [items, setItems] = useState<AppOfferBanner[]>([]);
  const [counts, setCounts] = useState<Record<string, BannerAnalyticsCounts>>({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<AppOfferBanner | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm);
  const [bannerBgUploadProgress, setBannerBgUploadProgress] = useState<number | null>(null);
  const bannerBgFileInputRef = useRef<HTMLInputElement>(null);
  const [bannerBgInlineMsg, setBannerBgInlineMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [reportBanner, setReportBanner] = useState<AppOfferBanner | null>(null);
  const [reportAnalyticsLoading, setReportAnalyticsLoading] = useState(false);
  const [reportAnalyticsError, setReportAnalyticsError] = useState<string | null>(null);
  const reportFetchGeneration = useRef(0);

  const inputClass = useMemo(
    () =>
      `w-full rounded-xl border px-3.5 py-2.5 text-sm transition shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500/50 ${
        isDark
          ? 'border-slate-600 bg-slate-900/80 text-slate-100'
          : 'border-slate-200 bg-white text-slate-900'
      }`,
    [isDark]
  );

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listAppOfferBannersAdmin();
      setItems(data);
      const next: Record<string, BannerAnalyticsCounts> = {};
      await Promise.all(
        data.map(async (b) => {
          try {
            next[b.id] = await getBannerEventCounts(b.id);
          } catch {
            next[b.id] = {
              impressions: 0,
              dismissClicks: 0,
              ctaClicks: 0,
              ctaClicksLink: 0,
              ctaClicksButton: 0,
              ctaClicksUpgradeModal: 0,
              ctaClicksLegacy: 0,
            };
          }
        })
      );
      setCounts(next);
    } catch (e) {
      console.error(e);
      showError('Failed to load offer banners');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    load();
  }, [load]);

  const openReport = useCallback(
    async (item: AppOfferBanner) => {
      setReportBanner(item);
      setReportAnalyticsError(null);
      setReportAnalyticsLoading(true);
      const gen = ++reportFetchGeneration.current;
      try {
        const c = await getBannerEventCounts(item.id);
        if (gen !== reportFetchGeneration.current) return;
        setCounts((prev) => ({ ...prev, [item.id]: c }));
        setReportAnalyticsError(null);
      } catch {
        if (gen !== reportFetchGeneration.current) return;
        setReportAnalyticsError('Could not refresh analytics.');
        showError('Could not load latest analytics for this banner.');
      } finally {
        if (gen === reportFetchGeneration.current) setReportAnalyticsLoading(false);
      }
    },
    [showError]
  );

  const reportBannerResolved = useMemo(() => {
    if (!reportBanner) return null;
    return items.find((x) => x.id === reportBanner.id) ?? reportBanner;
  }, [reportBanner, items]);

  useEffect(() => {
    if (!reportBanner || loading) return;
    if (!items.some((i) => i.id === reportBanner.id)) {
      reportFetchGeneration.current += 1;
      setReportBanner(null);
      setReportAnalyticsError(null);
      setReportAnalyticsLoading(false);
    }
  }, [reportBanner, items, loading]);

  const openAdd = () => {
    reportFetchGeneration.current += 1;
    setReportBanner(null);
    setReportAnalyticsError(null);
    setReportAnalyticsLoading(false);
    setEditing(null);
    setForm(defaultForm());
    setBannerBgInlineMsg(null);
    setSaveError(null);
    setShowModal(true);
  };

  const openEdit = (item: AppOfferBanner) => {
    reportFetchGeneration.current += 1;
    setReportBanner(null);
    setReportAnalyticsError(null);
    setReportAnalyticsLoading(false);
    setEditing(item);
    setBannerBgInlineMsg(null);
    setSaveError(null);
    setForm({
      isActive: item.isActive !== false,
      priority: item.priority ?? 0,
      startsAtInput: tsToDatetimeLocal(item.startsAt),
      endsAtInput: tsToDatetimeLocal(item.endsAt),
      pathRulesText: (item.pathRules || []).join('\n'),
      badgeText: item.badgeText || '',
      badgeBgColor: item.badgeBgColor || '#22c55e',
      badgeTextColor: item.badgeTextColor || '#ffffff',
      messageTextColor: item.messageTextColor || '#ffffff',
      ctaTextColor: item.ctaTextColor || '#ffffff',
      ctaButtonBorderColor: item.ctaButtonBorderColor || '#ffffff',
      backgroundCss: item.backgroundCss || '',
      backgroundImageUrl: item.backgroundImageUrl || '',
      message: item.message || '',
      promoCode: item.promoCode || '',
      ctaLabel: item.ctaLabel || '',
      ctaUrl: item.ctaUrl || '',
      ctaTarget: item.ctaTarget === 'upgrade_modal' ? 'upgrade_modal' : 'url',
      ctaStyle: item.ctaStyle === 'link' ? 'link' : 'button',
      contentAlign: normalizeContentAlign(item.contentAlign),
      showBadge: normalizeShowBadge(item.showBadge),
      showCta: normalizeShowCta(item.showCta),
      ctaButtonVariant: normalizeCtaButtonVariant(item.ctaButtonVariant),
      ctaButtonRadius: normalizeCtaButtonRadius(item.ctaButtonRadius),
      ctaButtonBgColor: (item.ctaButtonBgColor || '').trim() || 'rgba(255,255,255,0.22)',
      dismissible: item.dismissible !== false,
      dismissHideDays: clampDismissHideDays(item.dismissHideDays),
    });
    setShowModal(true);
  };

  const onBannerBackgroundFile = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const file = input.files?.[0];
      if (!file) return;

      setBannerBgInlineMsg(null);

      if (!isAllowedBannerBackgroundUpload(file)) {
        const msg = 'Please choose an image file (JPEG, PNG, WebP, GIF, AVIF, or HEIC).';
        setBannerBgInlineMsg({ tone: 'err', text: msg });
        showError(msg);
        input.value = '';
        return;
      }
      if (file.size > BANNER_BG_MAX_BYTES) {
        const msg = 'Image must be 5 MB or smaller.';
        setBannerBgInlineMsg({ tone: 'err', text: msg });
        showError(msg);
        input.value = '';
        return;
      }

      setBannerBgUploadProgress(0);
      try {
        const { publicUrl } = await uploadFileToS3(file, {
          // Under `blog-images/**` so writes match rules that are already deployed in most projects.
          // Top-level `app-offer-banners/` is also allowed in storage.rules after `firebase deploy --only storage`.
          keyPrefix: 'blog-images/app-offer-banners',
          onProgress: (pct) => setBannerBgUploadProgress(pct),
        });
        const trimmed = publicUrl.trim();
        const safe = safeBannerBackgroundImageUrl(trimmed);
        const finalUrl = safe ?? (/^https:\/\//i.test(trimmed) ? trimmed : null);
        if (!finalUrl) {
          const msg = 'Upload finished but the URL was not usable. Copy the link from Storage or paste a URL manually.';
          setBannerBgInlineMsg({ tone: 'err', text: msg });
          showError(msg);
          return;
        }
        setForm((prev) => ({ ...prev, backgroundImageUrl: finalUrl }));
        const okMsg = 'Image uploaded. The URL field and previews below are updated.';
        setBannerBgInlineMsg({ tone: 'ok', text: okMsg });
        showSuccess('Background image uploaded');
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Upload failed';
        setBannerBgInlineMsg({ tone: 'err', text: msg });
        showError(msg);
      } finally {
        setBannerBgUploadProgress(null);
        input.value = '';
      }
    },
    [showError, showSuccess]
  );

  const submit = async () => {
    if (!form.message.trim()) {
      showError('Message is required');
      return;
    }
    if (form.backgroundImageUrl.trim() && !safeBannerBackgroundImageUrl(form.backgroundImageUrl)) {
      showError('Background image must be a valid https URL, http URL, or a path starting with /');
      return;
    }
    if (form.showCta && form.ctaTarget === 'url') {
      if (form.ctaLabel.trim() && !form.ctaUrl.trim()) {
        showError('CTA URL is required when linking to a page or external site');
        return;
      }
      if (form.ctaUrl.trim()) {
        const ctaErr = validateCtaUrl(form.ctaUrl);
        if (ctaErr) {
          showError(ctaErr);
          return;
        }
      }
    }
    try {
      setIsSubmitting(true);
      setSaveError(null);
      const pathRules = parsePathRules(form.pathRulesText);
      const startsAt = datetimeLocalToTs(form.startsAtInput);
      const endsAt = datetimeLocalToTs(form.endsAtInput);
      const payload: AppOfferBannerInput = {
        isActive: form.isActive,
        priority: Number(form.priority) || 0,
        startsAt,
        endsAt,
        pathRules,
        badgeText: form.badgeText.trim(),
        badgeBgColor: form.badgeBgColor.trim(),
        badgeTextColor: form.badgeTextColor.trim(),
        messageTextColor: form.messageTextColor.trim(),
        ctaTextColor: form.ctaTextColor.trim(),
        ctaButtonBorderColor: form.ctaButtonBorderColor.trim(),
        backgroundCss: form.backgroundCss.trim(),
        backgroundImageUrl: form.backgroundImageUrl.trim(),
        message: form.message.trim(),
        promoCode: form.promoCode.trim(),
        ctaLabel: form.ctaLabel.trim(),
        ctaUrl: form.ctaTarget === 'upgrade_modal' ? '' : form.ctaUrl.trim(),
        ctaTarget: form.ctaTarget === 'upgrade_modal' ? 'upgrade_modal' : 'url',
        ctaStyle: form.ctaStyle === 'link' ? 'link' : 'button',
        contentAlign: form.contentAlign,
        showBadge: form.showBadge,
        showCta: form.showCta,
        ctaButtonVariant: normalizeCtaButtonVariant(form.ctaButtonVariant),
        ctaButtonRadius: normalizeCtaButtonRadius(form.ctaButtonRadius),
        ctaButtonBgColor: form.ctaButtonBgColor.trim(),
        dismissible: form.dismissible,
        dismissHideDays: clampDismissHideDays(form.dismissHideDays),
      };
      if (editing) {
        await updateAppOfferBanner(editing.id, payload);
        showSuccess('Banner updated');
      } else {
        await createAppOfferBanner(payload);
        showSuccess('Banner created');
      }
      setShowModal(false);
      await load();
    } catch (e) {
      console.error(e);
      const msg = formatFirestoreSaveError(e);
      setSaveError(msg);
      showError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this banner? Analytics events will remain.')) return;
    try {
      await deleteAppOfferBanner(id);
      showSuccess('Deleted');
      await load();
    } catch (e) {
      console.error(e);
      showError('Failed to delete');
    }
  };

  const toggleActive = async (item: AppOfferBanner) => {
    try {
      await updateAppOfferBanner(item.id, { isActive: !item.isActive });
      await load();
    } catch {
      showError('Failed to update status');
    }
  };

  const segmentBtn = (active: boolean) =>
    `flex-1 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
      active
        ? isDark
          ? 'bg-slate-700 text-white shadow-md'
          : 'bg-white text-slate-900 shadow-md'
        : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'
    }`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">Offer banners</h1>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-gray-600 dark:text-gray-400">
            Full-width bar below the app header. Use <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs dark:bg-slate-800">{'{{PROMO}}'}</code> when a promo code is set. Higher{' '}
            <span className="whitespace-nowrap">priority</span> wins. Analytics on each card are total logged events (not unique users).
          </p>
        </div>
        <button
          onClick={openAdd}
          className="inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-slate-900 to-slate-700 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-slate-900/25 transition hover:from-slate-800 hover:to-slate-600 dark:from-white dark:to-slate-200 dark:text-slate-900 dark:shadow-white/10 dark:hover:to-white"
        >
          New banner
        </button>
      </div>

      {loading ? (
        <div className="py-16 text-center text-sm text-slate-500 dark:text-slate-400">Loading banners…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 py-16 text-center text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-400">
          No banners yet. Create one to show offers in the app.
        </div>
      ) : (
        <div className="space-y-4">
          {items.map((item) => {
            const c = counts[item.id] || {
              impressions: 0,
              dismissClicks: 0,
              ctaClicks: 0,
              ctaClicksLink: 0,
              ctaClicksButton: 0,
              ctaClicksUpgradeModal: 0,
              ctaClicksLegacy: 0,
            };
            const ctaIsLink = item.ctaStyle === 'link';
            const ctaToUpgrade = item.ctaTarget === 'upgrade_modal';
            const showCtaRow = item.showCta !== false;
            const showBadgeRow = item.showBadge !== false;
            const hasRowCta =
              showCtaRow && Boolean(item.ctaLabel?.trim() && (ctaToUpgrade || item.ctaUrl?.trim()));
            const ctaRowPres =
              hasRowCta && !ctaIsLink
                ? getCtaButtonPresentation(
                    normalizeCtaButtonVariant(item.ctaButtonVariant),
                    normalizeCtaButtonRadius(item.ctaButtonRadius),
                    {
                      ctaTextColor: (item.ctaTextColor || '').trim() || '#ffffff',
                      ctaButtonBorderColor: (item.ctaButtonBorderColor || '').trim() || 'rgba(255,255,255,0.9)',
                      ctaButtonBgColor: (item.ctaButtonBgColor || '').trim(),
                    }
                  )
                : null;
            return (
              <div
                key={item.id}
                className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/50"
              >
                <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          item.isActive
                            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                        }`}
                      >
                        {item.isActive ? 'Active' : 'Draft'}
                      </span>
                      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Priority {item.priority ?? 0}</span>
                      {hasRowCta ? (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                          CTA: {ctaToUpgrade ? 'Modal' : ctaIsLink ? 'Link' : 'Button'}
                        </span>
                      ) : showCtaRow ? null : (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                          No CTA
                        </span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100 line-clamp-2">{item.message}</p>
                    <div className="flex flex-wrap gap-3 text-xs text-slate-500 dark:text-slate-400">
                      <span>{c.impressions} impressions</span>
                      <span>{c.dismissClicks} close clicks</span>
                      <span>
                        {c.ctaClicks} CTA clicks
                        {c.ctaClicks > 0 ? (
                          <span className="text-slate-400 dark:text-slate-500">
                            {' '}
                            (link {c.ctaClicksLink} · button {c.ctaClicksButton} · upgrade modal {c.ctaClicksUpgradeModal}
                            {c.ctaClicksLegacy > 0 ? ` · unlabeled ${c.ctaClicksLegacy}` : ''})
                          </span>
                        ) : null}
                      </span>
                      <span>{item.pathRules?.length ? item.pathRules.join(' · ') : 'All routes'}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => openEdit(item)}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void openReport(item)}
                      className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
                    >
                      Report
                    </button>
                    <button
                      onClick={() => toggleActive(item)}
                      className={`rounded-lg px-3 py-2 text-sm font-medium ${
                        item.isActive
                          ? 'bg-amber-50 text-amber-900 hover:bg-amber-100 dark:bg-amber-950/50 dark:text-amber-200'
                          : 'bg-emerald-50 text-emerald-900 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-200'
                      }`}
                    >
                      {item.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <button
                      onClick={() => remove(item.id)}
                      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800 hover:bg-red-100 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className="relative overflow-hidden border-t border-white/10">
                  <div
                    className="relative z-10 px-4 py-3 text-xs sm:text-sm"
                    style={{
                      ...offerBannerFillStyle(item.backgroundImageUrl || '', item.backgroundCss || ''),
                      color: (item.messageTextColor || '').trim() || '#ffffff',
                    }}
                  >
                    <div className={bannerOuterLayoutClass(normalizeContentAlign(item.contentAlign))}>
                      <div className={bannerContentClusterClass(normalizeContentAlign(item.contentAlign))}>
                        {showBadgeRow && item.badgeText?.trim() ? (
                          <span
                            className="mr-0 inline-flex shrink-0 self-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase"
                            style={{
                              backgroundColor: item.badgeBgColor || '#22c55e',
                              color: item.badgeTextColor || '#fff',
                            }}
                          >
                            {item.badgeText}
                          </span>
                        ) : null}
                        <span
                          className={`min-w-0 flex-1 opacity-95 ${bannerMessageTextClass(normalizeContentAlign(item.contentAlign))}`}
                        >
                          {item.message}
                        </span>
                      </div>
                      <div className={bannerActionsClusterClass(normalizeContentAlign(item.contentAlign))}>
                        {hasRowCta ? (
                          <span
                            className={
                              ctaIsLink
                                ? 'inline-flex items-center gap-1 text-xs font-medium underline underline-offset-2'
                                : (ctaRowPres?.className ?? '')
                            }
                            style={
                              ctaIsLink
                                ? {
                                    color: (item.ctaTextColor || '').trim() || '#ffffff',
                                    textDecorationColor: (item.ctaTextColor || '').trim() || '#ffffff',
                                  }
                                : ((ctaRowPres?.style ?? {}) as React.CSSProperties)
                            }
                          >
                            {item.ctaLabel.trim()}
                            {ctaToUpgrade ? null : <span aria-hidden>→</span>}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {reportBanner && reportBannerResolved ? (
        <BannerReportModal
          key={reportBanner.id}
          banner={reportBannerResolved}
          counts={counts[reportBanner.id]}
          analyticsRefreshing={reportAnalyticsLoading}
          analyticsError={reportAnalyticsError}
          onClose={() => {
            reportFetchGeneration.current += 1;
            setReportBanner(null);
            setReportAnalyticsError(null);
            setReportAnalyticsLoading(false);
          }}
          isDark={isDark}
        />
      ) : null}

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 md:p-6">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-md transition-opacity"
            aria-label="Close dialog"
            onClick={() => !isSubmitting && setShowModal(false)}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="offer-banner-modal-title"
            className={`relative flex max-h-[min(96vh,calc(100vh-0.5rem))] w-[min(96rem,calc(100vw-0.5rem))] max-w-[calc(100vw-0.5rem)] flex-col overflow-hidden rounded-2xl border shadow-2xl sm:max-h-[min(94vh,calc(100vh-1rem))] sm:w-[min(96rem,calc(100vw-1rem))] sm:max-w-[calc(100vw-1rem)] ${
              isDark ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200/80 bg-white text-slate-900'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <header
              className={`flex shrink-0 items-center justify-between gap-4 border-b px-5 py-4 sm:px-8 ${
                isDark ? 'border-slate-700 bg-slate-900/90' : 'border-slate-100 bg-gradient-to-r from-slate-50 to-white'
              }`}
            >
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-600 dark:text-cyan-400">Editor</p>
                <h2 id="offer-banner-modal-title" className="text-lg font-bold tracking-tight sm:text-xl">
                  {editing ? 'Edit banner' : 'New banner'}
                </h2>
              </div>
              <button
                type="button"
                onClick={() => !isSubmitting && setShowModal(false)}
                className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </header>

            <div
              className={`shrink-0 border-b px-5 pb-4 pt-4 sm:px-8 sm:pb-5 sm:pt-5 ${
                isDark ? 'border-slate-700 bg-slate-950/30' : 'border-slate-100 bg-slate-50/90'
              }`}
            >
              <BannerLivePreview form={form} isDark={isDark} />
              <p className="mt-3 text-center text-xs leading-relaxed text-slate-500 dark:text-slate-400 sm:text-left">
                Updates as you edit. Use Layout & optional blocks for alignment and to hide the badge or CTA. With CTA style set to Button, use Button design in Call to action for outline, solid, ghost, and corner radius.
              </p>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 sm:py-8">
              <div className="mx-auto max-w-5xl space-y-8">
                <section className="space-y-4">
                    <SectionTitle
                      icon={
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      }
                    >
                      Status & schedule
                    </SectionTitle>
                    <div
                      className={`space-y-4 rounded-2xl border p-4 sm:p-5 ${
                        isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-100 bg-slate-50/80'
                      }`}
                    >
                      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-transparent p-1 transition hover:border-slate-200 dark:hover:border-slate-600">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                          checked={form.isActive}
                          onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                        />
                        <span>
                          <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Active</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">Live when schedule, paths, and priority allow.</span>
                        </span>
                      </label>
                      <div className="grid gap-4 sm:grid-cols-3">
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Priority</label>
                          <input
                            type="number"
                            className={inputClass}
                            value={form.priority}
                            onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Starts</label>
                          <input
                            type="datetime-local"
                            className={inputClass}
                            value={form.startsAtInput}
                            onChange={(e) => setForm({ ...form, startsAtInput: e.target.value })}
                          />
                        </div>
                        <div>
                          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Ends</label>
                          <input
                            type="datetime-local"
                            className={inputClass}
                            value={form.endsAtInput}
                            onChange={(e) => setForm({ ...form, endsAtInput: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="space-y-4">
                    <SectionTitle
                      icon={
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                        </svg>
                      }
                    >
                      Targeting
                    </SectionTitle>
                    <div
                      className={`rounded-2xl border p-4 sm:p-5 ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-100 bg-slate-50/80'}`}
                    >
                      <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Path prefixes</label>
                      <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">One per line. Leave empty to show on all routes.</p>
                      <textarea
                        className={`${inputClass} min-h-[100px] resize-y font-mono text-xs leading-relaxed`}
                        value={form.pathRulesText}
                        onChange={(e) => setForm({ ...form, pathRulesText: e.target.value })}
                        placeholder={'/workspace/\n/account/billing'}
                      />
                    </div>
                  </section>

                  <section className="space-y-4">
                    <SectionTitle
                      icon={
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 5h16M4 12h16M4 19h10" />
                        </svg>
                      }
                    >
                      Layout & optional blocks
                    </SectionTitle>
                    <div
                      className={`space-y-4 rounded-2xl border p-4 sm:p-5 ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-100 bg-slate-50/80'}`}
                    >
                      <div>
                        <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">Content alignment</p>
                        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">How the badge, message, dismiss, and CTA sit in the bar (desktop wraps on small screens).</p>
                        <div className={`flex rounded-xl p-1 ${isDark ? 'bg-slate-900/80' : 'bg-slate-200/60'}`} role="group" aria-label="Content alignment">
                          {(['left', 'center', 'right'] as const).map((a) => (
                            <button
                              key={a}
                              type="button"
                              className={segmentBtn(form.contentAlign === a)}
                              onClick={() => setForm({ ...form, contentAlign: a })}
                            >
                              {a === 'left' ? 'Left' : a === 'center' ? 'Center' : 'Right'}
                            </button>
                          ))}
                        </div>
                      </div>
                      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-100 p-3 dark:border-slate-700">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                          checked={form.showBadge}
                          onChange={(e) => setForm({ ...form, showBadge: e.target.checked })}
                        />
                        <span>
                          <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Show badge</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">Turn off for text-only announcements. Label and colors below are ignored when off.</span>
                        </span>
                      </label>
                      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-100 p-3 dark:border-slate-700">
                        <input
                          type="checkbox"
                          className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                          checked={form.showCta}
                          onChange={(e) => setForm({ ...form, showCta: e.target.checked })}
                        />
                        <span>
                          <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Show call-to-action</span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">Turn off for announcements with no button or link.</span>
                        </span>
                      </label>
                    </div>
                  </section>

                  <section className="space-y-4">
                    <SectionTitle
                      icon={
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                        </svg>
                      }
                    >
                      Look & feel
                    </SectionTitle>
                    <div
                      className={`space-y-4 rounded-2xl border p-4 sm:p-5 ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-100 bg-slate-50/80'}`}
                    >
                      <div className={form.showBadge ? '' : 'opacity-50'}>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Badge label</label>
                        <input className={inputClass} value={form.badgeText} onChange={(e) => setForm({ ...form, badgeText: e.target.value })} />
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <ColorField label="Badge background" value={form.badgeBgColor} onChange={(v) => setForm({ ...form, badgeBgColor: v })} inputClass={inputClass} />
                        <ColorField label="Badge text" value={form.badgeTextColor} onChange={(v) => setForm({ ...form, badgeTextColor: v })} inputClass={inputClass} />
                      </div>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <ColorField
                          label="Message text"
                          value={form.messageTextColor}
                          onChange={(v) => setForm({ ...form, messageTextColor: v })}
                          inputClass={inputClass}
                        />
                        <ColorField
                          label="CTA text"
                          value={form.ctaTextColor}
                          onChange={(v) => setForm({ ...form, ctaTextColor: v })}
                          inputClass={inputClass}
                        />
                      </div>
                      <ColorField
                        label="CTA button border"
                        value={form.ctaButtonBorderColor}
                        onChange={(v) => setForm({ ...form, ctaButtonBorderColor: v })}
                        inputClass={inputClass}
                      />
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        Message color applies to the body copy, <code className="rounded bg-slate-200 px-1 dark:bg-slate-700">{'{{PROMO}}'}</code> chip text, and the dismiss control. CTA border is used when the CTA style is Button (link style ignores it).
                      </p>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Background (CSS)</label>
                        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                          Used when no background image is set (solid color or gradient).
                        </p>
                        <input
                          className={inputClass}
                          value={form.backgroundCss}
                          onChange={(e) => setForm({ ...form, backgroundCss: e.target.value })}
                          placeholder="linear-gradient(...) or #hex"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Background image (optional)</label>
                        <p className="mb-2 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{BANNER_BG_SIZE_HINT}</p>
                        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                          When set, this image fills the banner (<code className="rounded bg-slate-200 px-1 dark:bg-slate-700">cover</code>
                          ). You can upload or paste a URL (https or a path like{' '}
                          <code className="rounded bg-slate-200 px-1 dark:bg-slate-700">/your-image.jpg</code>). Clear the image to use CSS again.
                        </p>
                        <div className="mb-3 flex flex-wrap items-center gap-2">
                          <button
                            type="button"
                            disabled={bannerBgUploadProgress !== null || isSubmitting}
                            onClick={() => {
                              setBannerBgInlineMsg(null);
                              bannerBgFileInputRef.current?.click();
                            }}
                            className={`inline-flex items-center justify-center rounded-xl border px-3.5 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
                              isDark
                                ? 'border-slate-600 bg-slate-800 text-slate-100 hover:bg-slate-700'
                                : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
                            }`}
                          >
                            {bannerBgUploadProgress !== null ? `Uploading… ${bannerBgUploadProgress}%` : 'Upload image'}
                          </button>
                          {form.backgroundImageUrl.trim() ? (
                            <button
                              type="button"
                              disabled={bannerBgUploadProgress !== null || isSubmitting}
                              onClick={() => {
                                setForm({ ...form, backgroundImageUrl: '' });
                                setBannerBgInlineMsg(null);
                              }}
                              className="text-sm font-medium text-slate-600 underline decoration-slate-400 underline-offset-2 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                            >
                              Clear image
                            </button>
                          ) : null}
                        </div>
                        {bannerBgInlineMsg ? (
                          <p
                            className={`mb-3 text-sm font-medium ${
                              bannerBgInlineMsg.tone === 'err'
                                ? 'text-red-600 dark:text-red-400'
                                : 'text-emerald-600 dark:text-emerald-400'
                            }`}
                            role="status"
                          >
                            {bannerBgInlineMsg.text}
                          </p>
                        ) : null}
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Image URL (optional)</label>
                        <input
                          className={inputClass}
                          value={form.backgroundImageUrl}
                          onChange={(e) => setForm({ ...form, backgroundImageUrl: e.target.value })}
                          placeholder="Filled after upload, or paste https://… or /images/banner.jpg"
                        />
                        <p className="mb-1.5 mt-4 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Background preview (same as app bar)
                        </p>
                        <div
                          className={`overflow-hidden rounded-xl border shadow-sm ${
                            isDark ? 'border-slate-600' : 'border-slate-200'
                          }`}
                        >
                          <div
                            className={`min-h-[4.5rem] px-4 py-3 ${bannerOuterLayoutClass(normalizeContentAlign(form.contentAlign))}`}
                            style={offerBannerFillStyle(form.backgroundImageUrl, form.backgroundCss)}
                          >
                            <div className={bannerContentClusterClass(normalizeContentAlign(form.contentAlign))}>
                              {form.showBadge && form.badgeText.trim() ? (
                                <span
                                  className="inline-flex shrink-0 items-center self-center rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide sm:text-xs"
                                  style={{
                                    backgroundColor: form.badgeBgColor || '#22c55e',
                                    color: form.badgeTextColor || '#ffffff',
                                  }}
                                >
                                  {form.badgeText.trim()}
                                </span>
                              ) : null}
                              <p
                                className={`min-w-0 flex-1 text-xs leading-snug sm:text-sm ${bannerMessageTextClass(
                                  normalizeContentAlign(form.contentAlign)
                                )}`}
                                style={{ color: (form.messageTextColor || '').trim() || '#ffffff' }}
                              >
                                {previewMessage(
                                  form.message || '',
                                  form.promoCode || '',
                                  (form.messageTextColor || '').trim() || '#ffffff'
                                )}
                              </p>
                            </div>
                            <div className={bannerActionsClusterClass(normalizeContentAlign(form.contentAlign))}>
                              {form.dismissible ? (
                                <span className="rounded-md p-1.5 opacity-60" style={{ color: (form.messageTextColor || '').trim() || '#ffffff' }}>
                                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </span>
                              ) : null}
                              {form.showCta &&
                              form.ctaLabel.trim() &&
                              (form.ctaTarget === 'upgrade_modal' || form.ctaUrl.trim()) ? (
                                <span
                                  className={
                                    form.ctaStyle === 'link'
                                      ? 'text-xs font-medium underline underline-offset-2'
                                      : getCtaButtonPresentation(
                                          normalizeCtaButtonVariant(form.ctaButtonVariant),
                                          normalizeCtaButtonRadius(form.ctaButtonRadius),
                                          {
                                            ctaTextColor: (form.ctaTextColor || '').trim() || '#ffffff',
                                            ctaButtonBorderColor: (form.ctaButtonBorderColor || '').trim() || 'rgba(255,255,255,0.9)',
                                            ctaButtonBgColor: (form.ctaButtonBgColor || '').trim(),
                                          }
                                        ).className
                                  }
                                  style={
                                    (form.ctaStyle === 'link'
                                      ? {
                                          color: (form.ctaTextColor || '').trim() || '#ffffff',
                                          textDecorationColor: (form.ctaTextColor || '').trim() || '#ffffff',
                                        }
                                      : getCtaButtonPresentation(
                                          normalizeCtaButtonVariant(form.ctaButtonVariant),
                                          normalizeCtaButtonRadius(form.ctaButtonRadius),
                                          {
                                            ctaTextColor: (form.ctaTextColor || '').trim() || '#ffffff',
                                            ctaButtonBorderColor: (form.ctaButtonBorderColor || '').trim() || 'rgba(255,255,255,0.9)',
                                            ctaButtonBgColor: (form.ctaButtonBgColor || '').trim(),
                                          }
                                        ).style) as React.CSSProperties
                                  }
                                >
                                  {form.ctaLabel.trim()}
                                  {form.ctaTarget === 'upgrade_modal' ? null : <span aria-hidden>→</span>}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="space-y-4">
                    <SectionTitle
                      icon={
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h7" />
                        </svg>
                      }
                    >
                      Copy
                    </SectionTitle>
                    <div
                      className={`space-y-4 rounded-2xl border p-4 sm:p-5 ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-100 bg-slate-50/80'}`}
                    >
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Message</label>
                        <textarea className={`${inputClass} min-h-[120px] leading-relaxed`} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Promo code (optional)</label>
                        <input className={inputClass} value={form.promoCode} onChange={(e) => setForm({ ...form, promoCode: e.target.value })} />
                      </div>
                    </div>
                  </section>

                  <section className="space-y-4">
                    <SectionTitle
                      icon={
                        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                        </svg>
                      }
                    >
                      Call to action
                    </SectionTitle>
                    <div
                      className={`space-y-4 rounded-2xl border p-4 sm:p-5 ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-slate-100 bg-slate-50/80'} ${
                        !form.showCta ? 'opacity-55' : ''
                      }`}
                    >
                      <div>
                        <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">CTA destination</p>
                        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                          Open a page / external link, or the in-app upgrade modal (same as elsewhere in the product).
                        </p>
                        <div
                          className={`flex rounded-xl p-1 ${isDark ? 'bg-slate-900/80' : 'bg-slate-200/60'}`}
                          role="group"
                          aria-label="CTA destination"
                        >
                          <button
                            type="button"
                            className={segmentBtn(form.ctaTarget === 'url')}
                            onClick={() => setForm({ ...form, ctaTarget: 'url' })}
                          >
                            Page / URL
                          </button>
                          <button
                            type="button"
                            className={segmentBtn(form.ctaTarget === 'upgrade_modal')}
                            onClick={() => setForm({ ...form, ctaTarget: 'upgrade_modal' })}
                          >
                            Upgrade modal
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">CTA appearance</p>
                        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
                          Button vs text link. When Show call-to-action (under Layout) is off, nothing in this section appears in the app.
                        </p>
                        <div
                          className={`flex rounded-xl p-1 ${isDark ? 'bg-slate-900/80' : 'bg-slate-200/60'}`}
                          role="group"
                          aria-label="CTA style"
                        >
                          <button type="button" className={segmentBtn(form.ctaStyle === 'button')} onClick={() => setForm({ ...form, ctaStyle: 'button' })}>
                            Button
                          </button>
                          <button type="button" className={segmentBtn(form.ctaStyle === 'link')} onClick={() => setForm({ ...form, ctaStyle: 'link' })}>
                            Link
                          </button>
                        </div>
                      </div>
                      {form.ctaStyle === 'button' ? (
                        <>
                          <div>
                            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">Button design</p>
                            <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">Outline, solid fill, or minimal ghost. Link style ignores these.</p>
                            <div
                              className={`flex flex-wrap gap-1 rounded-xl p-1 ${isDark ? 'bg-slate-900/80' : 'bg-slate-200/60'}`}
                              role="group"
                              aria-label="Button variant"
                            >
                              {(['outline', 'solid', 'ghost'] as const).map((v) => (
                                <button
                                  key={v}
                                  type="button"
                                  className={`${segmentBtn(form.ctaButtonVariant === v)} capitalize`}
                                  onClick={() => setForm({ ...form, ctaButtonVariant: v })}
                                >
                                  {v}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">Button corners</p>
                            <div
                              className={`grid grid-cols-2 gap-1 rounded-xl p-1 sm:grid-cols-4 ${isDark ? 'bg-slate-900/80' : 'bg-slate-200/60'}`}
                              role="group"
                              aria-label="Button radius"
                            >
                              {(
                                [
                                  ['none', 'Sharp'],
                                  ['md', 'Medium'],
                                  ['lg', 'Large'],
                                  ['pill', 'Pill'],
                                ] as const
                              ).map(([val, label]) => (
                                <button
                                  key={val}
                                  type="button"
                                  className={segmentBtn(form.ctaButtonRadius === val)}
                                  onClick={() => setForm({ ...form, ctaButtonRadius: val })}
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          </div>
                          {form.ctaButtonVariant === 'solid' ? (
                            <ColorField
                              label="Button fill color"
                              value={form.ctaButtonBgColor}
                              onChange={(v) => setForm({ ...form, ctaButtonBgColor: v })}
                              inputClass={inputClass}
                            />
                          ) : null}
                        </>
                      ) : null}
                      <div className={`grid gap-4 ${form.ctaTarget === 'url' ? 'sm:grid-cols-2' : ''}`}>
                        <div className={form.ctaTarget === 'upgrade_modal' ? 'sm:col-span-2' : ''}>
                          <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">Label</label>
                          <input className={inputClass} value={form.ctaLabel} onChange={(e) => setForm({ ...form, ctaLabel: e.target.value })} placeholder="View plans" />
                        </div>
                        {form.ctaTarget === 'url' ? (
                          <div>
                            <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">URL</label>
                            <input
                              className={inputClass}
                              value={form.ctaUrl}
                              onChange={(e) => setForm({ ...form, ctaUrl: e.target.value })}
                              placeholder="/account/billing or https://…"
                            />
                          </div>
                        ) : (
                          <p className="text-xs leading-relaxed text-slate-500 dark:text-slate-400 sm:col-span-2">
                            URL is not used. The control opens the standard upgrade / plans modal.
                          </p>
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="space-y-3">
                    <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-100 p-4 dark:border-slate-700">
                      <input
                        type="checkbox"
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-cyan-600 focus:ring-cyan-500"
                        checked={form.dismissible}
                        onChange={(e) => setForm({ ...form, dismissible: e.target.checked })}
                      />
                      <span>
                        <span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Dismissible</span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          Users can close the bar. After they dismiss, it stays hidden in this browser for the number of days below, then it can appear again.
                        </span>
                      </span>
                    </label>
                    <div
                      className={`rounded-2xl border border-slate-100 p-4 dark:border-slate-700 ${
                        form.dismissible ? '' : 'opacity-50 pointer-events-none'
                      }`}
                    >
                      <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                        Hide again after dismiss (days)
                      </label>
                      <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                        Whole calendar days from when they close the banner (1–3650). Only applies when dismissible is on.
                      </p>
                      <input
                        type="number"
                        min={1}
                        max={3650}
                        step={1}
                        className={inputClass}
                        disabled={!form.dismissible}
                        value={form.dismissHideDays}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') {
                            setForm({ ...form, dismissHideDays: DEFAULT_DISMISS_HIDE_DAYS });
                            return;
                          }
                          const v = parseInt(raw, 10);
                          if (!Number.isFinite(v)) return;
                          setForm({ ...form, dismissHideDays: clampDismissHideDays(v) });
                        }}
                      />
                    </div>
                  </section>
              </div>
            </div>

            <footer
              className={`flex shrink-0 flex-col gap-2 border-t px-5 py-4 sm:px-8 ${
                isDark ? 'border-slate-700 bg-slate-900/95' : 'border-slate-100 bg-slate-50/90'
              }`}
            >
              {saveError ? (
                <p role="alert" className="text-sm text-red-600 dark:text-red-400">
                  {saveError}
                </p>
              ) : null}
              <div className="flex flex-col-reverse items-stretch justify-end gap-2 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={() => !isSubmitting && setShowModal(false)}
                className="rounded-xl px-5 py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-200/80 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isSubmitting}
                onClick={submit}
                className="rounded-xl bg-gradient-to-r from-cyan-600 to-cyan-700 px-6 py-2.5 text-sm font-semibold text-white shadow-lg shadow-cyan-900/20 transition hover:from-cyan-500 hover:to-cyan-600 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving…' : editing ? 'Save changes' : 'Create banner'}
              </button>
              </div>
            </footer>
          </div>
        </div>
      )}
      {showModal && typeof document !== 'undefined'
        ? createPortal(
            <input
              ref={bannerBgFileInputRef}
              type="file"
              accept="image/*"
              onChange={onBannerBackgroundFile}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: 2,
                height: 2,
                opacity: 0,
                zIndex: 2147483646,
              }}
              aria-hidden
              tabIndex={-1}
            />,
            document.body
          )
        : null}
    </div>
  );
};

export default AdminAppOfferBannerManager;
